/**
 * Offline cache — the IndexedDB half of the PWA story.
 *
 * Folders, per-mailbox summary pages, and recently read raw messages live
 * here so an offline reload still shows the mail the device has seen. The
 * server remains authoritative: every cached payload carries the
 * `uidValidity` it belongs to, and a mismatch invalidates the whole mailbox
 * (UIDs are meaningless across a validity change).
 *
 * Nothing in here is secret: no credentials, no passwords — bodies people
 * have already opened, on a browser profile that already had them.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import type {
  FolderInfo,
  MessageSummary,
  SendRequest,
} from "../bridge/types.ts";

/**
 * Database name; one per origin alongside the `accounts` database.
 */
const DB_NAME = "mail-cache";

/**
 * Schema version.
 */
const DB_VERSION = 1;

/**
 * How many recently-read raw messages to keep per mailbox.
 */
const RAW_KEEP = 25;

/**
 * Cached folder listing for one account.
 */
interface CachedFolders {
  /**
  Mailboxes as last fetched.
   */
  readonly folders: readonly FolderInfo[];
  /**
  `Date.now()` when written.
   */
  readonly cachedAt: number;
}

/**
 * One cached summary row (bridge wire shape, kept verbatim).
 */
type CachedSummary = MessageSummary;

/**
 * Cached summary pages for one account+mailbox.
 */
interface CachedPage {
  /**
  Mailbox `UIDVALIDITY` the rows belong to.
   */
  readonly uidValidity: number;
  /**
  Highest UID seen in the mailbox when cached.
   */
  readonly highestUid: number;
  /**
  Summary rows, ascending by UID.
   */
  readonly items: readonly CachedSummary[];
  /**
  True when the page walk reached the mailbox floor.
   */
  readonly complete: boolean;
  /**
  `Date.now()` when written.
   */
  readonly cachedAt: number;
}

/**
 * A raw message kept for offline re-reading.
 */
interface CachedRaw {
  /**
  The full RFC 5322 message.
   */
  readonly raw: ArrayBuffer;
  /**
  `Date.now()` when written; informational.
   */
  readonly cachedAt: number;
  /**
  Insertion sequence; drives the keep-newest prune.
   */
  readonly seq: number;
}

/**
 * A message the user asked to send while the bridge was unreachable.
 */
export interface QueuedSend {
  /**
  Auto-incremented queue position.
   */
  readonly key?: number;
  /**
  Account the message sends from.
   */
  readonly accountId: string;
  /**
  The outgoing message (no credentials — those stay memory-only).
   */
  readonly request: SendRequest;
  /**
  `Date.now()` when queued.
   */
  readonly queuedAt: number;
}

/**
 * A locally stored draft, keyed per account and compose context.
 */
export interface StoredDraft {
  /**
  Owning account id.
   */
  readonly accountId: string;
  /**
  `"new"` or `reply-<uid>`.
   */
  readonly draftKey: string;
  /**
  Recipients as typed (comma-separated).
   */
  readonly to: string;
  /**
  Copies as typed.
   */
  readonly cc: string;
  /**
  Subject line.
   */
  readonly subject: string;
  /**
  Body text.
   */
  readonly text: string;
  /**
  `Date.now()` when written.
   */
  readonly savedAt: number;
}

/**
 * Database shape; typed so `idb` returns real types.
 */
interface CacheDatabase extends DBSchema {
  /**
  Folder listing per account.
   */
  folders: {
    /**
    Account id.
     */
    readonly key: string;
    /**
    The cached listing.
     */
    readonly value: CachedFolders;
  };
  /**
  Summary pages, keyed `accountId|mailbox`.
   */
  pages: {
    /**
    Composite key.
     */
    readonly key: string;
    /**
    The cached page set.
     */
    readonly value: CachedPage;
  };
  /**
  Recently read raw messages, keyed `accountId|mailbox|uid`.
   */
  raw: {
    /**
    Composite key.
     */
    readonly key: string;
    /**
    The cached message.
     */
    readonly value: CachedRaw;
    /**
    Insertion-order index for the prune (key-only cursor reads).
     */
    readonly indexes: { "by-seq": number };
  };
  /**
  Outgoing messages awaiting a working connection.
   */
  outbox: {
    /**
    Auto-incremented queue key.
     */
    readonly key: number;
    /**
    The queued message.
     */
    readonly value: QueuedSend;
    /**
    Queue order per account.
     */
    readonly indexes: { "by-account": string };
  };
  /**
  Compose drafts, keyed `accountId|draftKey`.
   */
  drafts: {
    /**
    Composite key.
     */
    readonly key: string;
    /**
    The stored draft.
     */
    readonly value: StoredDraft;
  };
}

/**
 * Open database connection, one promise per page lifetime.
 */
let database: Promise<IDBPDatabase<CacheDatabase>> | null = null;

/**
 * Opens (and migrates) the cache database.
 *
 * @returns The shared connection promise.
 */
function db(): Promise<IDBPDatabase<CacheDatabase>> {
  // Module-lifetime memo of one connection, same pattern as accounts.ts.
  // eslint-disable-next-line unicorn/no-top-level-assignment-in-function -- deliberate single-connection memo
  database ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(data) {
      if (!data.objectStoreNames.contains("folders")) {
        data.createObjectStore("folders");
      }
      if (!data.objectStoreNames.contains("pages")) {
        data.createObjectStore("pages");
      }
      if (!data.objectStoreNames.contains("raw")) {
        const raw = data.createObjectStore("raw");
        raw.createIndex("by-seq", "seq");
      }
      if (!data.objectStoreNames.contains("outbox")) {
        const outbox = data.createObjectStore("outbox", {
          keyPath: "key",
          autoIncrement: true,
        });
        outbox.createIndex("by-account", "accountId");
      }
      if (!data.objectStoreNames.contains("drafts")) {
        data.createObjectStore("drafts");
      }
    },
  });
  return database;
}

/**
 * Persists a draft (whole-value replace).
 *
 * @param draft - The draft to store.
 */
export async function saveDraft(draft: StoredDraft): Promise<void> {
  const handle = await db();
  await handle.put("drafts", draft, `${draft.accountId}|${draft.draftKey}`);
}

/**
 * Reads one draft.
 *
 * @param accountId - Account id.
 * @param draftKey - `"new"` or `reply-<uid>`.
 * @returns The draft, or `null` when none is stored.
 */
export async function readDraft(
  accountId: string,
  draftKey: string,
): Promise<StoredDraft | null> {
  const handle = await db();
  const record = await handle.get("drafts", `${accountId}|${draftKey}`);
  return record ?? null;
}

/**
 * Deletes one draft (after sending, or when the user discards it).
 *
 * @param accountId - Account id.
 * @param draftKey - `"new"` or `reply-<uid>`.
 */
export async function deleteDraft(
  accountId: string,
  draftKey: string,
): Promise<void> {
  const handle = await db();
  await handle.delete("drafts", `${accountId}|${draftKey}`);
}

/**
 * Builds the pages-store key for an account's mailbox.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @returns The composite key.
 */
function pageKey(accountId: string, mailbox: string): string {
  return `${accountId}|${mailbox}`;
}

/**
 * Builds the raw-store key for one message.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @returns The composite key.
 */
function rawKey(accountId: string, mailbox: string, uid: number): string {
  return `${accountId}|${mailbox}|${uid}`;
}

/**
 * Reads the cached folder listing of an account.
 *
 * @param accountId - Account id.
 * @returns The listing, or `null` when nothing is cached.
 */
export async function cachedFolders(
  accountId: string,
): Promise<readonly FolderInfo[] | null> {
  const handle = await db();
  const record = await handle.get("folders", accountId);
  return record?.folders ?? null;
}

/**
 * Caches an account's folder listing.
 *
 * @param accountId - Account id.
 * @param folders - The listing to cache.
 */
export async function storeFolders(
  accountId: string,
  folders: readonly FolderInfo[],
): Promise<void> {
  const handle = await db();
  await handle.put("folders", { folders, cachedAt: Date.now() }, accountId);
}

/**
 * Reads the cached summary rows of one mailbox.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @returns The cached page set, or `null` when nothing is cached.
 */
export async function cachedPage(
  accountId: string,
  mailbox: string,
): Promise<CachedPage | null> {
  const handle = await db();
  const record = await handle.get("pages", pageKey(accountId, mailbox));
  return record ?? null;
}

/**
 * Writes the summary rows of one mailbox.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @param page - The page data to cache.
 * @param page.uidValidity - Mailbox `UIDVALIDITY` the rows belong to.
 * @param page.highestUid - Highest UID seen in the mailbox.
 * @param page.items - Summary rows, ascending by UID.
 * @param page.complete - Whether the page walk reached the mailbox floor.
 */
export async function storePage(
  accountId: string,
  mailbox: string,
  page: {
    readonly uidValidity: number;
    readonly highestUid: number;
    readonly items: readonly CachedSummary[];
    readonly complete: boolean;
  },
): Promise<void> {
  const handle = await db();
  await handle.put(
    "pages",
    { ...page, cachedAt: Date.now() },
    pageKey(accountId, mailbox),
  );
}

/**
 * Drops every cached payload tied to a mailbox — used when `uidValidity`
 * changed or the account was removed.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 */
export async function dropMailboxCache(
  accountId: string,
  mailbox: string,
): Promise<void> {
  const handle = await db();
  // `delete` on an absent key is a no-op, so no existence checks.
  await handle.delete("pages", pageKey(accountId, mailbox));
  const rawPrefix = `${accountId}|${mailbox}|`;
  const rawKeys = await handle.getAllKeys("raw");
  for (const key of rawKeys) {
    if (key.startsWith(rawPrefix)) {
      await handle.delete("raw", key);
    }
  }
}

/**
 * Reads a cached raw message.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @returns The raw bytes, or `null` when not cached.
 */
export async function cachedRaw(
  accountId: string,
  mailbox: string,
  uid: number,
): Promise<ArrayBuffer | null> {
  const handle = await db();
  const record = await handle.get("raw", rawKey(accountId, mailbox, uid));
  return record?.raw ?? null;
}

/**
 * Monotonic write sequence, seeded from the clock so ordering survives a
 * reload and stays deterministic within a session.
 */
let sequence = Date.now();

/**
 * Allocates the next insertion sequence number.
 *
 * @returns A strictly increasing number.
 */
function nextSequence(): number {
  // Module-lifetime counter; the assignment is the point of the function.
  // eslint-disable-next-line unicorn/no-top-level-assignment-in-function -- deliberate monotonic counter
  sequence += 1;
  return sequence;
}

/**
 * Caches a raw message and prunes the mailbox to the newest entries.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @param raw - The full RFC 5322 bytes.
 */
export async function storeRaw(
  accountId: string,
  mailbox: string,
  uid: number,
  raw: ArrayBuffer,
): Promise<void> {
  const handle = await db();
  const key = rawKey(accountId, mailbox, uid);
  await handle.put(
    "raw",
    { raw, cachedAt: Date.now(), seq: nextSequence() },
    key,
  );
  const prefix = `${accountId}|${mailbox}|`;
  // Key-only cursor: pruning never materialises message bodies.
  const keys: string[] = [];
  let cursor = await handle
    .transaction("raw")
    .store.index("by-seq")
    .openKeyCursor();
  while (cursor !== null) {
    const candidate = cursor.primaryKey;
    if (candidate.startsWith(prefix)) {
      keys.push(candidate);
    }
    cursor = await cursor.continue();
  }
  const stale = keys.slice(0, Math.max(0, keys.length - RAW_KEEP));
  for (const key of stale) {
    await handle.delete("raw", key);
  }
}

/**
 * Queues an outgoing message for later delivery.
 *
 * @param accountId - Account id.
 * @param request - The outgoing message.
 * @returns The queue key assigned to it.
 */
export async function queueSend(
  accountId: string,
  request: SendRequest,
): Promise<number> {
  const handle = await db();
  const key = await handle.add("outbox", {
    accountId,
    request,
    queuedAt: Date.now(),
  });
  return key;
}

/**
 * Lists an account's queued messages, oldest first.
 *
 * @param accountId - Account id.
 * @returns The queued messages.
 */
export async function listQueued(accountId: string): Promise<QueuedSend[]> {
  const handle = await db();
  const rows = await handle.getAllFromIndex("outbox", "by-account", accountId);
  return rows.toSorted((left, right) => left.queuedAt - right.queuedAt);
}

/**
 * Removes a queued message (delivered, or abandoned by the user).
 *
 * @param key - Queue key from {@link queueSend}.
 */
export async function removeQueued(key: number): Promise<void> {
  const handle = await db();
  await handle.delete("outbox", key);
}

/**
 * Clears every cache entry belonging to an account.
 *
 * @param accountId - Account id.
 */
export async function dropAccountCache(accountId: string): Promise<void> {
  const handle = await db();
  await handle.delete("folders", accountId);
  const pageKeys = await handle.getAllKeys("pages");
  for (const key of pageKeys) {
    if (key.startsWith(`${accountId}|`)) {
      await handle.delete("pages", key);
    }
  }
  const rawKeys = await handle.getAllKeys("raw");
  for (const key of rawKeys) {
    if (key.startsWith(`${accountId}|`)) {
      await handle.delete("raw", key);
    }
  }
  const queued = await handle.getAllFromIndex(
    "outbox",
    "by-account",
    accountId,
  );
  for (const row of queued) {
    if (row.key !== undefined) {
      await handle.delete("outbox", row.key);
    }
  }
}
